import { MouseEvent, ReactNode } from "react";

import {
  Alert,
  Box,
  Button,
  CircularProgress,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  Stack,
  Typography,
} from "@mui/material";
import { ArrowBack } from "@mui/icons-material";

import {
  formatTicketDate,
  TicketStatusChip,
  TicketTypeChip,
} from "components/support/TicketChips";
import {
  TICKET_DONE_DISCLAIMER,
  TicketScreenshot,
  useGetTicketQuery,
} from "features/support/tickets";

interface TicketDetailProps {
  id: number;
  onBack: () => void;
  onClose: () => void;
}

const SCREENSHOT_TYPES = ["image/png", "image/jpeg", "image/webp"];

/**
 * Browsers refuse to navigate a new tab to a data: URL, so a screenshot the
 * backend served inline is reopened as a blob URL instead. Done synchronously
 * inside the click, or the popup blocker treats the new tab as unrequested.
 */
const openScreenshot = (
  event: MouseEvent<HTMLAnchorElement>,
  screenshot: TicketScreenshot,
) => {
  const match = screenshot.url.match(/^data:([^;,]+)?(;base64)?,([\s\S]*)$/);
  if (!match) {
    return;
  }
  event.preventDefault();
  const [, type = "", base64, payload] = match;
  // A blob URL runs in the app's own origin: only ever open an image type.
  if (!SCREENSHOT_TYPES.includes(type)) {
    return;
  }
  const text = base64 ? atob(payload) : decodeURIComponent(payload);
  const bytes = Uint8Array.from(text, (char) => char.charCodeAt(0));
  const objectUrl = URL.createObjectURL(new Blob([bytes], { type }));
  window.open(objectUrl, "_blank", "noopener");
};

const Field = ({ label, children }: { label: string; children: ReactNode }) => (
  <Box>
    <Typography variant="caption" color="text.secondary" component="div">
      {label}
    </Typography>
    <Typography variant="body2" component="div">
      {children}
    </Typography>
  </Box>
);

const TicketDetail = ({ id, onBack, onClose }: TicketDetailProps) => {
  const { data: ticket, isLoading, isError } = useGetTicketQuery(id);

  const renderBody = () => {
    if (isLoading) {
      return (
        <Box display="flex" justifyContent="center" py={4}>
          <CircularProgress aria-label="Loading ticket" />
        </Box>
      );
    }
    if (isError || !ticket) {
      return <Alert severity="error">Failed to load the ticket.</Alert>;
    }
    return (
      <Stack spacing={2}>
        {ticket.status === "Done" && (
          <Alert severity="info">{TICKET_DONE_DISCLAIMER}</Alert>
        )}
        {ticket.status === "Error" && (
          <Alert severity="error">
            {ticket.error_message || "Processing this ticket failed."}
          </Alert>
        )}
        <Stack direction="row" spacing={3} flexWrap="wrap" useFlexGap>
          <Field label="Type">
            <TicketTypeChip type={ticket.type} />
          </Field>
          <Field label="Status">
            <TicketStatusChip status={ticket.status} />
          </Field>
          <Field label="Created">
            {formatTicketDate(ticket.created_on, true)}
          </Field>
          {ticket.updated_on && (
            <Field label="Updated">
              {formatTicketDate(ticket.updated_on, true)}
            </Field>
          )}
        </Stack>
        <Stack direction="row" spacing={3} flexWrap="wrap" useFlexGap>
          <Field label="Submitted by">{ticket.creator_email}</Field>
          <Field label="Operating system">{ticket.operating_system}</Field>
          <Field label="Browser">{ticket.browser}</Field>
        </Stack>
        <Field label="Description">
          <Box sx={{ whiteSpace: "pre-wrap", overflowWrap: "anywhere" }}>
            {ticket.description}
          </Box>
        </Field>
        {!!ticket.screenshots.length && (
          <Field label="Screenshots">
            <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
              {ticket.screenshots.map((screenshot) => (
                <a
                  key={screenshot.id}
                  href={screenshot.url}
                  target="_blank"
                  rel="noopener noreferrer"
                  onClick={(event) => openScreenshot(event, screenshot)}
                  title={`Open ${screenshot.filename}`}
                >
                  <Box
                    component="img"
                    src={screenshot.url}
                    alt={screenshot.filename}
                    sx={{
                      width: 120,
                      height: 80,
                      objectFit: "cover",
                      borderRadius: 1,
                      border: 1,
                      borderColor: "divider",
                      display: "block",
                    }}
                  />
                </a>
              ))}
            </Stack>
          </Field>
        )}
      </Stack>
    );
  };

  return (
    <>
      <DialogTitle sx={{ display: "flex", alignItems: "center", gap: 1 }}>
        <IconButton aria-label="Back to tickets" onClick={onBack} edge="start">
          <ArrowBack />
        </IconButton>
        Ticket #{id}
      </DialogTitle>
      <DialogContent dividers>{renderBody()}</DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Close</Button>
      </DialogActions>
    </>
  );
};

export default TicketDetail;
